import { ReactElement, useEffect, useRef, useState } from 'react';

import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

import { MIME_TYPES, MimeType } from '../../enums/MimeEnum';
import { useAttachmentMutators } from '../../storageMutators/useAttachmentMutators';
import { getFileUrl, isImage, isImageFile } from '../../utils/utilsFiles';

export type FileUploadType = 'image' | 'pdf';

interface FilePathUrl {
  name: string;
  url: string;
  displayName?: string;
  thumbnailUrl?: string | null;
  contentType?: string;
}

interface GratiFileUploadProps {
  onFileDeleted?: (fileName: string) => void;
  onFileAdded?: (fileName: string, contentType?: string) => void;
  fileNames?: string[];
  storageFilePrefix?: string | null;
  storageFileSuffix?: string | null;
  storageLocation: string;
  maxNumFiles?: number;
  label?: string | null;
  thumbnailSize?: 'small' | 'medium' | 'large';
  allowedTypes?: MimeType[];
  defaultDisplayName?: string;
  thumbnailSuffix?: string | null;
  renderExtraRowContent?: (fileName: string) => ReactElement | null;
}

// --- Module-level helpers ---

function stripFileExtension(fileName: string): string {
  return fileName.replace(/\.[^/.]+$/, '');
}

// --- FileInput ---

interface FileInputProps {
  uploadInputRef: React.RefObject<HTMLInputElement | null>;
  allowedTypes: MimeType[];
  isUploading: boolean;
  isDeleting: boolean;
  filePathUrl?: FilePathUrl;
  onFileSelected: (file: File, filePathUrl?: FilePathUrl) => void;
}

function FileInput({ uploadInputRef, allowedTypes, isUploading, isDeleting, filePathUrl, onFileSelected }: FileInputProps): ReactElement {
  return (
    <>
      <input
        ref={uploadInputRef}
        type="file"
        accept={allowedTypes.join(',')}
        aria-label="Upload file"
        style={{ display: 'none' }}
        onChange={(event) => {
          if (event.target.files && event.target.files.length > 0) {
            onFileSelected(event.target.files[0], filePathUrl);
          }
        }}
      />
      {filePathUrl ? (
        <IconButton
          aria-label="Replace file"
          onClick={() => uploadInputRef.current && uploadInputRef.current.click()}
          color="primary"
          disabled={isUploading || isDeleting}
        >
          <EditIcon />
        </IconButton>
      ) : (
        <Button
          onClick={() => uploadInputRef.current && uploadInputRef.current.click()}
          variant="outlined"
          size="small"
          color="primary"
          startIcon={<AttachFileIcon />}
          disabled={isUploading || isDeleting}
        >
          Add
        </Button>
      )}
    </>
  );
}

// --- FileRow ---

interface FileRowProps {
  filePathUrl: FilePathUrl;
  thumbnailSize: 'small' | 'medium' | 'large';
  isUploading: boolean;
  isDeleting: boolean;
  uploadInputRef: React.RefObject<HTMLInputElement | null>;
  allowedTypes: MimeType[];
  renderExtraRowContent?: (fileName: string) => ReactElement | null;
  onFileSelected: (file: File, filePathUrl?: FilePathUrl) => void;
  onFileDeleted: (fileName: string) => void;
}

function FileRow({
  filePathUrl,
  thumbnailSize,
  isUploading,
  isDeleting,
  uploadInputRef,
  allowedTypes,
  renderExtraRowContent,
  onFileSelected,
  onFileDeleted,
}: FileRowProps): ReactElement {
  const [cancelConfirm, setCancelConfirm] = useState(false);

  const isImg = isImageFile(filePathUrl.contentType, filePathUrl.url);
  const avatarSrc = isImg ? (filePathUrl.thumbnailUrl || filePathUrl.url) : undefined;
  const avatarPx = thumbnailSize === 'small' ? 40 : thumbnailSize === 'medium' ? 60 : 80;

  return (
    <Stack useFlexGap spacing={2} direction="row" sx={{ alignItems: 'center' }}>
      {isImg ? (
        <Avatar src={avatarSrc} sx={{ width: avatarPx, height: avatarPx }} />
      ) : (
        <Typography
          variant="bodyLarge"
          sx={{
            minWidth: 100,
            color: 'primary.main',
            textDecoration: 'underline',
            cursor: 'pointer',
            '&:hover': { color: 'primary.dark' },
          }}
          onClick={() => {
            const link = document.createElement('a');
            link.href = filePathUrl.url;
            link.download = filePathUrl.displayName || stripFileExtension(filePathUrl.name);
            link.target = '_blank';
            link.click();
          }}
        >
          {filePathUrl.displayName || stripFileExtension(filePathUrl.name)}
        </Typography>
      )}
      {renderExtraRowContent?.(filePathUrl.name)}
      <FileInput
        uploadInputRef={uploadInputRef}
        allowedTypes={allowedTypes}
        isUploading={isUploading}
        isDeleting={isDeleting}
        filePathUrl={filePathUrl}
        onFileSelected={onFileSelected}
      />
      <IconButton
        aria-label="Delete file"
        onClick={() => setCancelConfirm(true)}
        color="secondary"
        disabled={isUploading || isDeleting}
      >
        <DeleteIcon />
      </IconButton>
      {cancelConfirm && (
        <Stack direction="row" spacing={1}>
          <Button
            variant="text"
            size="small"
            color="secondary"
            onClick={() => { onFileDeleted(filePathUrl.name); setCancelConfirm(false); }}
            disabled={isUploading || isDeleting}
          >
            Confirm
          </Button>
          <Button
            variant="text"
            size="small"
            color="secondary"
            onClick={() => setCancelConfirm(false)}
            disabled={isUploading || isDeleting}
          >
            Cancel
          </Button>
        </Stack>
      )}
    </Stack>
  );
}

// --- GratiFileUpload ---

export default function GratiFileUpload(props: GratiFileUploadProps) {
  const {
    onFileDeleted,
    onFileAdded,
    fileNames = [],
    storageFilePrefix = null,
    storageFileSuffix = null,
    storageLocation,
    maxNumFiles = 1,
    label = 'Upload File',
    thumbnailSize = 'medium',
    allowedTypes = [MIME_TYPES.JPEG, MIME_TYPES.PNG, MIME_TYPES.GIF, MIME_TYPES.WEBP, MIME_TYPES.PDF],
    defaultDisplayName,
    thumbnailSuffix = null,
    renderExtraRowContent,
  } = props;

  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const initializedRef = useRef(false);
  const [filePathsUrls, setFilePathsUrls] = useState<FilePathUrl[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pendingFile, setPendingFile] = useState<{ file: File; displayName: string } | null>(null);

  const { storeFile, deleteFile, getFileMetadata } = useAttachmentMutators();

  useEffect(() => {
    const initializeFiles = async () => {
      if (fileNames.length > 0 && !initializedRef.current) {
        initializedRef.current = true;
        const urls = await Promise.all(
          fileNames.map(async (name) => {
            try {
              const url = await getFileUrl(storageLocation, name);
              const metadata = await getFileMetadata(storageLocation, name);
              const contentType = metadata?.contentType;
              const thumbnailUrl = thumbnailSuffix && isImage(contentType ?? '')
                ? await getFileUrl(storageLocation, name + thumbnailSuffix).catch(() => null)
                : null;
              return {
                name,
                url,
                displayName: metadata?.customMetadata?.displayName,
                contentType,
                thumbnailUrl,
              };
            } catch (error) {
              console.error('Error getting file URL:', error);
              return null;
            }
          })
        );
        setFilePathsUrls(urls.filter((url): url is NonNullable<typeof url> => url !== null));
      }
    };
    initializeFiles();
  }, [fileNames, storageLocation, getFileMetadata, thumbnailSuffix]);

  const handleFileSelected = (file: File, filePathUrl?: FilePathUrl) => {
    if (isImage(file.type) || (maxNumFiles === 1 && defaultDisplayName)) {
      handleFileAdded(file, filePathUrl, isImage(file.type) ? file.name : defaultDisplayName);
    } else {
      setPendingFile({
        file,
        displayName: defaultDisplayName || stripFileExtension(file.name),
      });
    }
  };

  const handleFileAdded = async (file: File, filePathUrl?: FilePathUrl, displayName?: string) => {
    if (!filePathUrl && filePathsUrls.length >= maxNumFiles) {
      console.error(`Maximum number of files (${maxNumFiles}) reached`);
      return;
    }

    setIsUploading(true);
    try {
      const { fileName, url } = await storeFile(file, storageLocation, storageFilePrefix, storageFileSuffix, {
        contentType: file.type,
        customMetadata: displayName ? { displayName } : {},
        displayName: displayName || file.name,
        size: file.size.toString(),
        timeCreated: new Date().toISOString(),
        updated: new Date().toISOString(),
      });
      setFilePathsUrls((prev) => [...prev, { name: fileName, url, displayName, contentType: file.type, thumbnailUrl: null }]);
      onFileAdded?.(fileName, file.type);
    } catch (error) {
      console.error('Error uploading file:', error);
    } finally {
      setIsUploading(false);
      setPendingFile(null);
    }
  };

  const handleFileDeleted = async (fileName: string) => {
    setIsDeleting(true);
    try {
      await deleteFile(storageLocation, fileName);
      setFilePathsUrls((prev) => prev.filter((file) => file.name !== fileName));
      onFileDeleted?.(fileName);
    } catch (error) {
      console.error('Error deleting file:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Stack useFlexGap spacing={2} sx={{ alignItems: 'flex-start' }}>
      {label && <Typography variant="bodyLarge">{label}</Typography>}
      {filePathsUrls.map((filePathUrl) => (
        <FileRow
          key={filePathUrl.name}
          filePathUrl={filePathUrl}
          thumbnailSize={thumbnailSize}
          isUploading={isUploading}
          isDeleting={isDeleting}
          uploadInputRef={uploadInputRef}
          allowedTypes={allowedTypes}
          renderExtraRowContent={renderExtraRowContent}
          onFileSelected={handleFileSelected}
          onFileDeleted={handleFileDeleted}
        />
      ))}
      {filePathsUrls.length < maxNumFiles && !pendingFile && (
        <FileInput
          uploadInputRef={uploadInputRef}
          allowedTypes={allowedTypes}
          isUploading={isUploading}
          isDeleting={isDeleting}
          onFileSelected={handleFileSelected}
        />
      )}
      {pendingFile && (
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
          <TextField
            autoFocus
            size="small"
            label="Display Name"
            value={pendingFile.displayName}
            onChange={(e) => setPendingFile({ ...pendingFile, displayName: e.target.value })}
          />
          <Button
            variant="contained"
            onClick={() => handleFileAdded(pendingFile.file, undefined, pendingFile.displayName)}
            disabled={isUploading || isDeleting}
          >
            Upload
          </Button>
          <Button variant="text" onClick={() => setPendingFile(null)} disabled={isUploading || isDeleting}>
            Cancel
          </Button>
        </Stack>
      )}
    </Stack>
  );
}
